import { useState } from "react";
import type { FunctionComponent, FormEvent } from "react";
import { linkStyles } from "./linkStyles";
import type { LinkResult, Platform } from "./types";

interface Props {
  readonly onLink: (username: string, platform: string) => Promise<LinkResult>;
  readonly onSuccess: (result: { rating: number | null; coachLevel: string | null }) => void;
}

export const LinkStep: FunctionComponent<Props> = ({ onLink, onSuccess }) => {
  const [platform, setPlatform] = useState<Platform>("chess.com");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) { setError("Enter a username."); return; }
    setLoading(true);
    setError("");
    const result = await onLink(trimmed, platform);
    setLoading(false);
    if (result.success) {
      onSuccess({ rating: result.rating ?? null, coachLevel: result.coachLevel ?? null });
    } else {
      setError(result.error || "Failed to verify username.");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={linkStyles.platformToggle}>
        {(["chess.com", "lichess"] as Platform[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => { setPlatform(p); setError(""); }}
            style={{
              ...linkStyles.platformButton,
              ...(platform === p ? linkStyles.platformButtonActive : {}),
            }}
          >
            {p === "chess.com" ? "Chess.com" : "Lichess"}
          </button>
        ))}
      </div>

      <input
        type="text"
        value={username}
        onChange={(e) => { setUsername(e.target.value); setError(""); }}
        placeholder={platform === "chess.com" ? "Chess.com username" : "Lichess username"}
        style={linkStyles.input}
        autoFocus
        disabled={loading}
      />

      {error && <div style={linkStyles.error}>{error}</div>}

      <button
        type="submit"
        disabled={loading || !username.trim()}
        style={{
          ...linkStyles.submitButton,
          opacity: loading || !username.trim() ? 0.5 : 1,
        }}
      >
        {loading ? "Verifying..." : "Verify & Link"}
      </button>
    </form>
  );
};
